import React, { useState, useEffect } from 'react';
import { useVisitTracker } from '../../hooks/useVisitTracker';

const ConversionUnlocker = () => {
  useVisitTracker('conversion-unlocker');
  
  const [step, setStep] = useState(0);
  const [journeyStage, setJourneyStage] = useState('');
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);

  // Scroll to top when showing results
  useEffect(() => {
    if (step === 3) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [step]);
  
  // Touchpoint selection options
  const touchpoints = [
    { id: 'checkout', label: 'Checkout Process' },
    { id: 'signup', label: 'Sign-up/Registration' },
    { id: 'product', label: 'Product Selection' },
    { id: 'landing', label: 'Landing Page' },
    { id: 'pricing', label: 'Pricing Page' }
  ];
  
  // Assessment questions based on selected touchpoint
  const questions = {
    checkout: [
      { 
        id: 'abandonment', 
        text: 'What percentage of users abandon during the checkout process?',
        options: [
          { value: 'low', label: 'Less than 20%' },
          { value: 'medium', label: '20-40%' },
          { value: 'high', label: '40-60%' },
          { value: 'very_high', label: 'More than 60%' }
        ]
      },
      { 
        id: 'engagement', 
        text: 'How long do users typically spend on the checkout page before abandoning?',
        options: [
          { value: 'immediate', label: 'They leave almost immediately (under 10 seconds)' },
          { value: 'brief', label: 'Brief period (10-30 seconds)' },
          { value: 'engaged', label: 'They engage with the form (30-60 seconds)' },
          { value: 'very_engaged', label: 'Extended period (over 60 seconds)' }
        ]
      },
      { 
        id: 'fields', 
        text: 'At which point in the form completion do most users abandon?',
        options: [
          { value: 'beginning', label: 'At the beginning/first few fields' },
          { value: 'personal', label: 'When asked for personal information' },
          { value: 'payment', label: 'When asked for payment information' },
          { value: 'submit', label: 'At the final submission step' }
        ]
      },
      { 
        id: 'return_rate', 
        text: 'What percentage of users who abandon checkout return later to complete the purchase?',
        options: [
          { value: 'few', label: 'Very few (less than 10%)' },
          { value: 'some', label: 'Some (10-25%)' },
          { value: 'many', label: 'Many (25-50%)' },
          { value: 'most', label: 'Most (more than 50%)' }
        ]
      },
      { 
        id: 'comparison', 
        text: 'Do users frequently return to product or pricing pages during checkout?',
        options: [
          { value: 'never', label: 'Rarely or never' },
          { value: 'sometimes', label: 'Sometimes' },
          { value: 'often', label: 'Often' },
          { value: 'very_often', label: 'Very frequently' }
        ]
      }
    ],
    signup: [
      { 
        id: 'completion_rate', 
        text: 'What percentage of users complete the sign-up process?',
        options: [
          { value: 'very_low', label: 'Less than 20%' },
          { value: 'low', label: '20-40%' },
          { value: 'medium', label: '40-60%' },
          { value: 'high', label: 'More than 60%' }
        ]
      },
      { 
        id: 'field_completion', 
        text: 'Do users complete all optional fields or mostly just required fields?',
        options: [
          { value: 'required_only', label: 'Mostly just required fields' },
          { value: 'some_optional', label: 'Required and some optional fields' },
          { value: 'most_optional', label: 'Required and most optional fields' },
          { value: 'all_fields', label: 'Almost all fields, required and optional' }
        ]
      },
      { 
        id: 'hesitation', 
        text: 'Where do users spend the most time during sign-up?',
        options: [
          { value: 'personal_info', label: 'Personal information fields' },
          { value: 'account_creation', label: 'Username/password creation' },
          { value: 'preferences', label: 'Preference selection' },
          { value: 'terms', label: 'Terms & conditions/privacy policy' }
        ]
      },
      { 
        id: 'social_signup', 
        text: 'If you offer social sign-up options, what percentage of users choose these over traditional signup?',
        options: [
          { value: 'not_applicable', label: 'N/A - We don\'t offer social sign-up options' },
          { value: 'mostly_traditional', label: 'Mostly traditional (>75% traditional)' },
          { value: 'more_traditional', label: 'More traditional (50-75% traditional)' },
          { value: 'more_social', label: 'More social (50-75% social)' },
          { value: 'mostly_social', label: 'Mostly social (>75% social)' }
        ]
      },
      { 
        id: 'post_signup', 
        text: 'After signing up, what percentage of users take the next desired action (make a purchase, complete profile, etc.)?',
        options: [
          { value: 'very_few', label: 'Very few (<20%)' },
          { value: 'some', label: 'Some (20-40%)' },
          { value: 'many', label: 'Many (40-60%)' },
          { value: 'most', label: 'Most (>60%)' }
        ]
      }
    ],
    product: [
      { 
        id: 'time_spent', 
        text: 'How much time do users typically spend on product pages?',
        options: [
          { value: 'very_brief', label: 'Very brief (<30 seconds)' },
          { value: 'brief', label: 'Brief (30-60 seconds)' },
          { value: 'moderate', label: 'Moderate (1-3 minutes)' },
          { value: 'extended', label: 'Extended (>3 minutes)' }
        ]
      },
      { 
        id: 'comparison', 
        text: 'How many products do users typically view before making a selection?',
        options: [
          { value: 'single', label: 'Single product only' },
          { value: 'few', label: '2-3 products' },
          { value: 'several', label: '4-6 products' },
          { value: 'many', label: 'More than 6 products' }
        ]
      },
      { 
        id: 'engagement', 
        text: 'Which product information do users engage with most?',
        options: [
          { value: 'images', label: 'Product images/gallery' },
          { value: 'description', label: 'Product description' },
          { value: 'specifications', label: 'Technical specifications' },
          { value: 'reviews', label: 'Customer reviews' }
        ]
      },
      { 
        id: 'cart_rate', 
        text: 'What percentage of product page visitors add the item to cart?',
        options: [
          { value: 'very_low', label: 'Very low (<10%)' },
          { value: 'low', label: 'Low (10-20%)' },
          { value: 'medium', label: 'Medium (20-40%)' },
          { value: 'high', label: 'High (>40%)' }
        ]
      },
      { 
        id: 'questions', 
        text: 'Do customers frequently ask questions about products before purchasing?',
        options: [
          { value: 'rarely', label: 'Rarely or never' },
          { value: 'occasionally', label: 'Occasionally' },
          { value: 'frequently', label: 'Frequently' },
          { value: 'very_frequently', label: 'Very frequently' }
        ]
      }
    ],
    landing: [
      { 
        id: 'bounce_rate', 
        text: 'What is the bounce rate for your landing page?',
        options: [
          { value: 'low', label: 'Low (<30%)' },
          { value: 'medium', label: 'Medium (30-50%)' },
          { value: 'high', label: 'High (50-70%)' },
          { value: 'very_high', label: 'Very high (>70%)' }
        ]
      },
      { 
        id: 'time_on_page', 
        text: 'How long do visitors typically spend on your landing page?',
        options: [
          { value: 'very_brief', label: 'Very brief (<20 seconds)' },
          { value: 'brief', label: 'Brief (20-40 seconds)' },
          { value: 'moderate', label: 'Moderate (40-60 seconds)' },
          { value: 'extended', label: 'Extended (>60 seconds)' }
        ]
      },
      { 
        id: 'scroll_depth', 
        text: 'How far do visitors typically scroll down your landing page?',
        options: [
          { value: 'top_only', label: 'Above the fold only' },
          { value: 'quarter', label: 'About 25% of the page' },
          { value: 'half', label: 'About 50% of the page' },
          { value: 'full', label: 'Most or all of the page' }
        ]
      },
      { 
        id: 'cta_clicks', 
        text: 'What percentage of visitors click your primary call-to-action?',
        options: [
          { value: 'very_low', label: 'Very low (<5%)' },
          { value: 'low', label: 'Low (5-10%)' },
          { value: 'medium', label: 'Medium (10-20%)' },
          { value: 'high', label: 'High (>20%)' }
        ]
      },
      { 
        id: 'return_visits', 
        text: 'What percentage of landing page visitors return for a second visit?',
        options: [
          { value: 'very_few', label: 'Very few (<10%)' },
          { value: 'some', label: 'Some (10-20%)' },
          { value: 'moderate', label: 'Moderate (20-30%)' },
          { value: 'many', label: 'Many (>30%)' }
        ]
      }
    ],
    pricing: [
      { 
        id: 'time_spent', 
        text: 'How much time do visitors typically spend on the pricing page?',
        options: [
          { value: 'very_brief', label: 'Very brief (<30 seconds)' },
          { value: 'brief', label: 'Brief (30-60 seconds)' },
          { value: 'moderate', label: 'Moderate (1-2 minutes)' },
          { value: 'extended', label: 'Extended (>2 minutes)' }
        ]
      },
      { 
        id: 'comparison', 
        text: 'Do visitors actively compare different pricing tiers/packages?',
        options: [
          { value: 'single_focus', label: 'Mostly focus on a single option' },
          { value: 'light_comparison', label: 'Some comparison between options' },
          { value: 'detailed_comparison', label: 'Detailed comparison between options' },
          { value: 'extensive_comparison', label: 'Switch repeatedly between options' }
        ]
      },
      { 
        id: 'exit_rate', 
        text: 'What is the exit rate from your pricing page?',
        options: [
          { value: 'low', label: 'Low (<30%)' },
          { value: 'medium', label: 'Medium (30-50%)' },
          { value: 'high', label: 'High (50-70%)' },
          { value: 'very_high', label: 'Very high (>70%)' }
        ]
      },
      { 
        id: 'engagement', 
        text: 'Which pricing information do visitors engage with most?',
        options: [
          { value: 'price_points', label: 'Basic price points' },
          { value: 'features', label: 'Feature comparisons' },
          { value: 'discounts', label: 'Discounts/special offers' },
          { value: 'faq', label: 'Pricing FAQs/additional info' }
        ]
      },
      { 
        id: 'conversion', 
        text: 'What percentage of pricing page visitors proceed to purchase/signup?',
        options: [
          { value: 'very_low', label: 'Very low (<10%)' },
          { value: 'low', label: 'Low (10-20%)' },
          { value: 'medium', label: 'Medium (20-30%)' },
          { value: 'high', label: 'High (>30%)' }
        ]
      }
    ]
  };
  
  // Psychological barrier analysis logic
  const analyzeResponses = (touchpoint, responses) => {
    // Analysis based on touchpoint and answers
    const barriers = {
      checkout: {
        'Loss Aversion': 0,
        'Trust Deficit': 0,
        'Decision Paralysis': 0,
        'Effort/Value Imbalance': 0,
        'Status Quo Bias': 0
      },
      signup: {
        'Privacy Concerns': 0,
        'Trust Deficit': 0,
        'Future Commitment Anxiety': 0,
        'Perceived Complexity': 0,
        'Value Uncertainty': 0
      },
      product: {
        'Choice Overload': 0,
        'Information Asymmetry': 0,
        'Social Proof Deficit': 0,
        'Decision Fatigue': 0,
        'Analysis Paralysis': 0
      },
      landing: {
        'Value Proposition Clarity': 0,
        'Cognitive Overload': 0,
        'Trust Signals': 0,
        'Relevance Perception': 0,
        'Action Clarity': 0
      },
      pricing: {
        'Value Perception': 0,
        'Price Anchoring Issues': 0,
        'Comparison Complexity': 0,
        'Future Value Uncertainty': 0,
        'Loss Aversion': 0
      }
    };
    
    // Checkout analysis logic
    if (touchpoint === 'checkout') {
      if (responses.abandonment === 'high' || responses.abandonment === 'very_high') {
        barriers.checkout['Trust Deficit'] += 2;
        barriers.checkout['Loss Aversion'] += 1;
      }
      
      if (responses.engagement === 'engaged' || responses.engagement === 'very_engaged') {
        barriers.checkout['Decision Paralysis'] += 2;
        barriers.checkout['Effort/Value Imbalance'] += 1;
      }
      
      if (responses.fields === 'payment') {
        barriers.checkout['Loss Aversion'] += 3;
        barriers.checkout['Trust Deficit'] += 2;
      } else if (responses.fields === 'personal') {
        barriers.checkout['Trust Deficit'] += 3;
        barriers.checkout['Privacy Concerns'] += 2;
      }
      
      if (responses.return_rate === 'few' || responses.return_rate === 'some') {
        barriers.checkout['Status Quo Bias'] += 2;
        barriers.checkout['Loss Aversion'] += 1;
      }
      
      if (responses.comparison === 'often' || responses.comparison === 'very_often') {
        barriers.checkout['Effort/Value Imbalance'] += 3;
        barriers.checkout['Decision Paralysis'] += 2;
      }
    }
    
    // Signup analysis logic
    if (touchpoint === 'signup') {
      if (responses.completion_rate === 'very_low' || responses.completion_rate === 'low') {
        barriers.signup['Perceived Complexity'] += 2;
        barriers.signup['Value Uncertainty'] += 2;
      }
      
      if (responses.field_completion === 'required_only') {
        barriers.signup['Privacy Concerns'] += 3;
        barriers.signup['Future Commitment Anxiety'] += 1;
      }
      
      if (responses.hesitation === 'personal_info') {
        barriers.signup['Privacy Concerns'] += 3;
        barriers.signup['Trust Deficit'] += 2;
      } else if (responses.hesitation === 'terms') {
        barriers.signup['Trust Deficit'] += 3;
        barriers.signup['Future Commitment Anxiety'] += 2;
      }
      
      if (responses.social_signup === 'mostly_traditional' || responses.social_signup === 'more_traditional') {
        barriers.signup['Privacy Concerns'] += 2;
        barriers.signup['Trust Deficit'] += 1;
      }
      
      if (responses.post_signup === 'very_few' || responses.post_signup === 'some') {
        barriers.signup['Value Uncertainty'] += 3;
        barriers.signup['Future Commitment Anxiety'] += 1;
      }
    }
    
    // Product selection analysis logic
    if (touchpoint === 'product') {
      if (responses.time_spent === 'moderate' || responses.time_spent === 'extended') {
        barriers.product['Information Asymmetry'] += 2;
        barriers.product['Decision Fatigue'] += 1;
      }
      
      if (responses.comparison === 'several' || responses.comparison === 'many') {
        barriers.product['Choice Overload'] += 3;
        barriers.product['Analysis Paralysis'] += 2;
      }
      
      if (responses.engagement === 'reviews') {
        barriers.product['Social Proof Deficit'] += 3;
        barriers.product['Trust Deficit'] += 1;
      } else if (responses.engagement === 'specifications') {
        barriers.product['Information Asymmetry'] += 2;
        barriers.product['Analysis Paralysis'] += 1;
      }
      
      if (responses.cart_rate === 'very_low' || responses.cart_rate === 'low') {
        barriers.product['Decision Fatigue'] += 2;
        barriers.product['Choice Overload'] += 1;
      }
      
      if (responses.questions === 'frequently' || responses.questions === 'very_frequently') {
        barriers.product['Information Asymmetry'] += 3;
        barriers.product['Trust Deficit'] += 1;
      }
    }
    
    // Landing page analysis logic
    if (touchpoint === 'landing') {
      if (responses.bounce_rate === 'high' || responses.bounce_rate === 'very_high') {
        barriers.landing['Value Proposition Clarity'] += 3;
        barriers.landing['Relevance Perception'] += 2;
      }
      
      if (responses.time_on_page === 'very_brief' || responses.time_on_page === 'brief') {
        barriers.landing['Value Proposition Clarity'] += 2;
        barriers.landing['Trust Signals'] += 1;
      }
      
      if (responses.scroll_depth === 'top_only' || responses.scroll_depth === 'quarter') {
        barriers.landing['Value Proposition Clarity'] += 3;
        barriers.landing['Cognitive Overload'] += 1;
      }
      
      if (responses.cta_clicks === 'very_low' || responses.cta_clicks === 'low') {
        barriers.landing['Action Clarity'] += 3;
        barriers.landing['Value Proposition Clarity'] += 1;
      }
      
      if (responses.return_visits === 'very_few' || responses.return_visits === 'some') {
        barriers.landing['Relevance Perception'] += 2;
        barriers.landing['Value Proposition Clarity'] += 1;
      }
    }
    
    // Pricing page analysis logic
    if (touchpoint === 'pricing') {
      if (responses.time_spent === 'moderate' || responses.time_spent === 'extended') {
        barriers.pricing['Comparison Complexity'] += 2;
        barriers.pricing['Price Anchoring Issues'] += 1;
      }
      
      if (responses.comparison === 'detailed_comparison' || responses.comparison === 'extensive_comparison') {
        barriers.pricing['Value Perception'] += 2;
        barriers.pricing['Comparison Complexity'] += 3;
      }
      
      if (responses.exit_rate === 'high' || responses.exit_rate === 'very_high') {
        barriers.pricing['Value Perception'] += 3;
        barriers.pricing['Loss Aversion'] += 2;
      }
      
      if (responses.engagement === 'features') {
        barriers.pricing['Value Perception'] += 2;
        barriers.pricing['Future Value Uncertainty'] += 1;
      } else if (responses.engagement === 'discounts') {
        barriers.pricing['Price Anchoring Issues'] += 3;
        barriers.pricing['Value Perception'] += 1;
      }
      
      if (responses.conversion === 'very_low' || responses.conversion === 'low') {
        barriers.pricing['Value Perception'] += 2;
        barriers.pricing['Loss Aversion'] += 3;
      }
    }
    
    // Find the top barrier and second barrier
    const barrierScores = barriers[touchpoint];
    const sortedBarriers = Object.entries(barrierScores)
      .sort((a, b) => b[1] - a[1])
      .filter(([_, score]) => score > 0)
      .map(([barrier]) => barrier);
    
    const primaryBarrier = sortedBarriers[0] || 'No significant barrier detected';
    const secondaryBarrier = sortedBarriers[1] || '';
    
    // Calculate the revenue impact
    const impactDescriptions = {
      checkout: "Removing this barrier could increase checkout completion by 15-30%",
      signup: "Addressing this barrier could improve sign-up completion by 20-35%",
      product: "Resolving this barrier could increase product selection conversion by 25-40%",
      landing: "Eliminating this barrier could reduce bounce rates by 20-30%",
      pricing: "Addressing this barrier could improve pricing page conversion by 15-25%"
    };
    
    const conversionImpacts = {
      checkout: 0.225, // Average of 15-30%
      signup: 0.275,  // Average of 20-35%
      product: 0.325, // Average of 25-40%
      landing: 0.25,  // Average of 20-30%
      pricing: 0.20   // Average of 15-25%
    };
    
    return {
      primaryBarrier,
      secondaryBarrier,
      description: getBarrierDescription(primaryBarrier, touchpoint),
      secondaryDescription: secondaryBarrier ? getBarrierDescription(secondaryBarrier, touchpoint) : '',
      impactDescription: impactDescriptions[touchpoint],
      conversionImpact: conversionImpacts[touchpoint]
    };
  };
  
  // Description of each psychological barrier
  const getBarrierDescription = (barrier, touchpoint) => {
    const descriptions = {
      'Loss Aversion': "Users feel the 'pain' of paying more strongly than the anticipated pleasure of having your product. This creates psychological friction during payment steps. They hesitate most when asked for payment information because the act of parting with money triggers a stronger psychological response than the anticipated benefit.",
      'Decision Paralysis': "Users struggle to make a final decision due to fear of making the wrong choice, causing them to delay or abandon the process entirely.",
      'Trust Deficit': "Users have concerns about the security, privacy, or legitimacy of your process, creating psychological resistance to completion.",
      'Effort/Value Imbalance': "Users perceive the effort required to complete the process as disproportionate to the perceived value they'll receive.",
      'Status Quo Bias': "Users have a psychological preference to keep things as they are rather than make a change, even when change would be beneficial.",
      'Privacy Concerns': "Users are hesitant to share personal information due to concerns about how their data will be used or protected.",
      'Future Commitment Anxiety': "Users worry about being locked into future obligations or unwanted communications after signing up.",
      'Perceived Complexity': "The process appears more complicated or time-consuming than users are willing to invest in at this stage.",
      'Value Uncertainty': "Users aren't convinced that the value they'll receive justifies the information or commitment they're providing.",
      'Choice Overload': "Too many options or product variations create cognitive overload, making decision-making more difficult.",
      'Information Asymmetry': "Users feel they lack sufficient information to make a confident decision, creating uncertainty and hesitation.",
      'Social Proof Deficit': "Users don't see enough evidence that others like them have made this choice and been satisfied.",
      'Decision Fatigue': "After viewing multiple products or options, mental energy for decision-making becomes depleted, leading to abandonment.",
      'Analysis Paralysis': "Too much detailed information causes users to get stuck in endless comparison without reaching a decision.",
      'Value Proposition Clarity': "Users don't quickly understand what you're offering and why it matters to them.",
      'Cognitive Overload': "Too many elements, messages, or options overwhelm users' ability to process information effectively.",
      'Trust Signals': "Insufficient indicators of credibility and trustworthiness create psychological resistance.",
      'Relevance Perception': "Users don't immediately recognize how your offering addresses their specific needs or situation.",
      'Action Clarity': "Users aren't clear about what action they should take next or why they should take it.",
      'Price Anchoring Issues': "The way prices are presented creates unfavorable comparison points in users' minds.",
      'Comparison Complexity': "The structure of pricing options makes meaningful comparison difficult, creating decision friction.",
      'Future Value Uncertainty': "Users struggle to envision the long-term value they'll receive relative to the cost."
    };
    
    return descriptions[barrier] || "This psychological barrier creates friction in your customer journey.";
  };
  
  // Demo functionality - set up demo data for the checkout process
  const setupDemoData = () => {
    setJourneyStage('checkout');
    const demoAnswers = {
      abandonment: 'high',
      engagement: 'engaged',
      fields: 'payment',
      return_rate: 'few',
      comparison: 'often'
    };
    
    setAnswers(demoAnswers);
    const result = analyzeResponses('checkout', demoAnswers);
    setResults(result);
    setStep(3);
  };
  
  // Handle answer selection
  const handleAnswerSelect = (questionId, value) => {
    setAnswers({
      ...answers,
      [questionId]: value
    });
  };
  
  // Handle form submission and generate results
  const calculateResults = () => {
    const result = analyzeResponses(journeyStage, answers);
    setResults(result);
    setStep(3);
  };
  
  // Reset the assessment
  const resetAssessment = () => {
    setStep(0);
    setJourneyStage('');
    setAnswers({});
    setResults(null);
  };
  
  // Revenue Calculator Component
  const RevenueCalculator = () => {
    const [conversionRate, setConversionRate] = useState(3.5);
    const [avgOrderValue, setAvgOrderValue] = useState(85);
    const [monthlyVisitors, setMonthlyVisitors] = useState(12000);
    
    // Calculate revenue impact based on conversion improvements
    const calculateImpact = () => {
      if (!results) return { current: 0, potential: 0, difference: 0 };
      
      const currentRevenue = (conversionRate / 100) * avgOrderValue * monthlyVisitors;
      const improvedConversion = (conversionRate / 100) * (1 + results.conversionImpact);
      const potentialRevenue = improvedConversion * avgOrderValue * monthlyVisitors;
      
      return {
        current: Math.round(currentRevenue),
        potential: Math.round(potentialRevenue),
        difference: Math.round(potentialRevenue - currentRevenue)
      };
    };
    
    const impact = calculateImpact();
    
    return (
      <div className="bg-gray-100 p-6 rounded-lg">
        <h3 className="text-xl font-bold mb-4">Estimate Your Revenue Impact</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium mb-1">Current Conversion Rate (%)</label>
            <input 
              type="number" 
              min="0.1" 
              max="100" 
              step="0.1" 
              value={conversionRate} 
              onChange={(e) => setConversionRate(parseFloat(e.target.value))}
              className="w-full p-2 border rounded"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Average Order Value ($)</label>
            <input 
              type="number" 
              min="1" 
              step="1" 
              value={avgOrderValue} 
              onChange={(e) => setAvgOrderValue(parseInt(e.target.value))}
              className="w-full p-2 border rounded"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Monthly Visitors</label>
            <input 
              type="number" 
              min="100" 
              step="100" 
              value={monthlyVisitors} 
              onChange={(e) => setMonthlyVisitors(parseInt(e.target.value))}
              className="w-full p-2 border rounded"
            />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded border">
          <div className="grid grid-cols-1 gap-4">
            <div className="flex justify-between items-center border-b pb-3">
              <p className="text-gray-600">Current Monthly Revenue</p>
              <p className="text-xl font-bold">${impact.current.toLocaleString()}</p>
            </div>
            
            <div className="flex justify-between items-center border-b pb-3">
              <p className="text-gray-600">Potential Monthly Revenue</p>
              <p className="text-xl font-bold">${impact.potential.toLocaleString()}</p>
            </div>
            
            <div className="flex justify-between items-center">
              <p className="text-gray-600">Monthly Revenue Opportunity</p>
              <p className="text-xl font-bold text-green-600">+${impact.difference.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-blue-800">Conversion Unlocker</h1>
        {journeyStage && step === 3 && (
          <p className="text-gray-600">Results for {touchpoints.find(t => t.id === journeyStage)?.label}</p>
        )}
      </div>
      
      {/* Progress Steps */}
      <div className="flex justify-between items-center mb-8">
        <div className={`flex flex-col items-center ${step >= 0 ? 'text-blue-600' : 'text-gray-400'}`}>
          <div className={`h-8 w-8 rounded-full flex items-center justify-center ${step >= 0 ? 'bg-blue-100 text-blue-600 border-2 border-blue-600' : 'bg-gray-200 text-gray-400'}`}>1</div>
          <p className="text-sm mt-1">Select Touchpoint</p>
        </div>
        <div className="flex-1 h-0.5 mx-2 bg-blue-600"></div>
        <div className={`flex flex-col items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
          <div className={`h-8 w-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-100 text-blue-600 border-2 border-blue-600' : 'bg-gray-200 text-gray-400'}`}>2</div>
          <p className="text-sm mt-1">Answer Questions</p>
        </div>
        <div className="flex-1 h-0.5 mx-2 bg-blue-600"></div>
        <div className={`flex flex-col items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
          <div className={`h-8 w-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-blue-100 text-blue-600 border-2 border-blue-600' : 'bg-gray-200 text-gray-400'}`}>3</div>
          <p className="text-sm mt-1">View Results</p>
        </div>
      </div>
      
      {/* Step 1: Touchpoint Selection */}
      {step === 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Select a Customer Journey Touchpoint</h2>
          <p className="text-gray-600 mb-6">Choose one specific touchpoint where you're experiencing conversion challenges or customer hesitation.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {touchpoints.map((point) => (
              <button
                key={point.id}
                className={`p-4 border rounded-lg text-left transition ${journeyStage === point.id ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
                onClick={() => setJourneyStage(point.id)}
              >
                <h3 className="font-medium">{point.label}</h3>
              </button>
            ))}
          </div>
          
          <div className="mt-8 flex justify-end">
            {journeyStage === 'checkout' && (
              <button
                className="px-6 py-2 mr-2 border border-blue-600 text-blue-600 rounded hover:bg-blue-50"
                onClick={setupDemoData}
              >
                Demo Results
              </button>
            )}
            <button
              className="px-6 py-2 bg-blue-600 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
              disabled={!journeyStage}
              onClick={() => setStep(1)}
            >
              Continue
            </button>
          </div>
        </div>
      )}
      
      {/* Step 2: Assessment Questions */}
      {step === 1 && journeyStage && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">
            {touchpoints.find(t => t.id === journeyStage)?.label} Assessment
          </h2>
          <p className="text-gray-600 mb-6">Answer these questions about customer behavior at this touchpoint.</p>
          
          <div className="space-y-8">
            {questions[journeyStage]?.map((question, index) => (
              <div key={question.id} className="border-b pb-6">
                <p className="font-medium mb-3">{index + 1}. {question.text}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {question.options.map((option) => (
                    <button
                      key={option.value}
                      className={`p-3 border rounded text-left transition ${answers[question.id] === option.value ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-blue-200'}`}
                      onClick={() => handleAnswerSelect(question.id, option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-8 flex justify-between">
            <button
              className="px-6 py-2 border border-gray-300 rounded"
              onClick={() => setStep(0)}
            >
              Back
            </button>
            <button
              className="px-6 py-2 bg-blue-600 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
              disabled={Object.keys(answers).length < (questions[journeyStage]?.length || 0)}
              onClick={calculateResults}
            >
              View Results
            </button>
          </div>
        </div>
      )}
      
      {/* Step 3: Results */}
      {step === 3 && results && (
        <div>
          <h2 className="text-xl font-semibold mb-2">Your Results</h2>
          <p className="text-gray-600 mb-6">Based on your responses, we've identified the primary conversion blocker affecting your {touchpoints.find(t => t.id === journeyStage)?.label.toLowerCase()}.</p>
          
          {/* Primary Barrier Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <h3 className="text-xl font-bold text-blue-800 mb-2">Primary Conversion Blocker: {results.primaryBarrier}</h3>
            <p className="mb-4">{results.description}</p>
            <p className="font-medium">{results.impactDescription}</p>
          </div>
          
          {/* Single Secondary Barrier */}
          {results.secondaryBarrier && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-2">Additional Conversion Factor</h3>
              <div className="flex items-start mb-2">
                <div className="h-2 w-2 rounded-full bg-gray-600 mt-2 mr-2"></div>
                <div>
                  <span className="font-medium">{results.secondaryBarrier}:</span> {results.secondaryDescription}
                </div>
              </div>
            </div>
          )}
          
          {/* Revenue Calculator */}
          <RevenueCalculator />
          
          {/* Full Assessment CTA - with scrollable container if needed */}
          <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg overflow-auto" style={{ maxHeight: '300px' }}>
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-2">Want a Comprehensive Assessment?</h3>
              <p className="mb-4">
                This mini-assessment identifies one conversion blocker at a single touchpoint. 
                For a comprehensive analysis of all conversion blockers across your entire customer journey, 
                contact us about our full Conversion Analysis report.
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Our complete analysis includes:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 mb-6">
                <li>In-depth identification of all conversion blockers</li>
                <li>Detailed recommendations for addressing each blocker</li>
                <li>Prioritized implementation roadmap</li>
                <li>Competitive psychological positioning analysis</li>
              </ul>
            </div>
          </div>
          
          <div className="mt-8 flex justify-center space-x-4">
            <button
              className="px-6 py-2 border border-gray-300 rounded"
              onClick={resetAssessment}
            >
              Start Over
            </button>
            
            <a 
              href="https://kronologic.me/1-click/zWxqVT2ASOytpMCtSReRDWxbBRVHiUbHjzqAm5x3Arg" 
              className="px-6 py-2 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition"
            >
              Let's talk
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversionUnlocker;